/**
 * Which product has a page of its own, and where it lives.
 *
 * Kept apart from `links.ts` because that module reaches for `usePathname`, which makes it a
 * client module — and this map is also needed on the server, by the lead endpoint.
 */

/**
 * Dedicated product pages. Products without an entry link to their section on the homepage.
 *
 * They sit at the site root — /finance, not /products/finance. `/products` is the index that
 * lists them all. next.config.ts redirects the old /products/<slug> URLs here, so anything
 * already indexed or used in an ad still arrives.
 */
export const PRODUCT_PAGES: Record<string, string> = {
  projects: "/projects",
  scale: "/scale",
  crm: "/crm",
  people: "/people",
  insights: "/insights",
  social: "/social",
  seo: "/seo",
  chat: "/chat",
  skill: "/skill",
  attendance: "/attendance",
  finance: "/finance",
  purchase: "/purchase",
  docs: "/docs",
  sheets: "/sheets",
  presentation: "/slides",
  forms: "/forms",
  signs: "/signs",
};

/** The product page a path belongs to, as its URL slug — `/slides` -> "slides". */
export function productSlugOf(pathname: string | null) {
  if (!pathname) return undefined;
  return Object.values(PRODUCT_PAGES)
    .find((url) => pathname === url)
    ?.split("/")
    .pop();
}

/**
 * In-page anchors (`#pricing`) only work on the page that owns them. Shared chrome (nav,
 * footer) links to homepage sections, so off the homepage those become `/#pricing`.
 */
export function resolveHref(href: string, pathname: string | null) {
  if (!href.startsWith("#") || pathname === "/") return href;
  // "#top" is the top of the homepage, which is exactly where "/" already lands. Off the
  // homepage it therefore resolves to the bare path: coming home from a product page gives
  // "/", not "/#top" — a fragment that describes nothing the path does not already say.
  return href === "#top" ? "/" : `/${href}`;
}
